import React, { FC, useEffect, useMemo, useRef } from 'react';
import { RE_DIGIT } from '../../utils/common';
import './style.scss';

interface VerificationCodeInputProps {
  value: string;
  valueLength: number;
  handleOnChange: (value: string) => void;
}

const VerificationCodeInput: FC<VerificationCodeInputProps> = ({
  value,
  valueLength,
  handleOnChange,
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const valueItems = useMemo(() => {
    const valueArray = value.split('');
    const items: Array<string> = [];

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i];

      if (RE_DIGIT.test(char)) {
        items.push(char);
      } else {
        items.push('');
      }
    }

    return items;
  }, [value, valueLength]);

  useEffect(() => {
    if (inputRefs.current.length > 0) inputRefs.current[0].focus();
  }, []);

  const getPreviousInput = (target: HTMLElement): HTMLInputElement | null => {
    if (inputRefs.current.length > 0) {
      for (let i = 0; i < inputRefs.current.length; i++) {
        if (inputRefs.current[i] === target && i - 1 >= 0) {
          return inputRefs.current[i - 1];
        }
      }
    }
    return null;
  };

  const getNextInput = (target: HTMLElement): HTMLInputElement | null => {
    if (inputRefs.current.length > 0) {
      for (let i = 0; i < inputRefs.current.length; i++) {
        if (
          inputRefs.current[i] === target &&
          i + 1 < inputRefs.current.length
        ) {
          return inputRefs.current[i + 1];
        }
      }
    }
    return null;
  };

  const focusToNextInput = (target: HTMLElement) => {
    const nextInput = getNextInput(target);
    nextInput && nextInput.focus();
  };

  const focusToPrevInput = (target: HTMLElement) => {
    const previousInput = getPreviousInput(target);
    previousInput && previousInput.focus();
  };

  const inputOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number,
  ) => {
    const target = e.target;
    let targetValue = target.value.trim();
    const isTargetValueDigit = RE_DIGIT.test(targetValue);

    if (!isTargetValueDigit && targetValue !== '') {
      return;
    }

    const nextInput = getNextInput(target);

    if (!isTargetValueDigit && nextInput && nextInput.value !== '') {
      return;
    }

    targetValue = isTargetValueDigit ? targetValue : ' ';

    const targetValueLength = targetValue.length;

    if (targetValueLength === 1) {
      const newValue =
        value.substring(0, idx) + targetValue + value.substring(idx + 1);

      handleOnChange(newValue);

      if (!isTargetValueDigit) {
        return;
      }

      focusToNextInput(target);
    } else if (targetValueLength === valueLength) {
      handleOnChange(targetValue);

      target.blur();
    }
  };
  const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const target = e.target as HTMLInputElement;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault();
      return focusToNextInput(target);
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault();
      return focusToPrevInput(target);
    }

    const targetValue = target.value;

    if (e.key !== 'Backspace' || targetValue !== '') {
      return;
    }

    focusToPrevInput(target);
  };

  const inputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { target } = e;
    const prevInput = getPreviousInput(target);
    if (prevInput && prevInput.value === '') {
      return prevInput.focus();
    }
  };

  return (
    <>
      {valueItems.map((digit, idx) => (
        <div key={idx} className="emma-verification-code-input">
          <input
            ref={(el) => (inputRefs.current[idx] = el)}
            className="emma-stylesheet-input-field"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{1}"
            maxLength={valueLength}
            value={digit}
            onChange={(e) => inputOnChange(e, idx)}
            onKeyDown={inputOnKeyDown}
            onFocus={inputOnFocus}
          />
        </div>
      ))}
    </>
  );
};

export default VerificationCodeInput;
